import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import Header from './components/Header';
import SearchBar from './components/SearchBar';
import WeatherCard from './components/WeatherCard';
import InteractiveMap from './components/InteractiveMap';
import TodaysHighlights from './components/TodaysHighlights';
import ControlButtons from './components/ControlButtons';
import FavoritesTab from './components/FavoritesTab';
// import SearchTest from './components/SearchTest';
import { useLocation } from './hooks/useLocation';
import { useWeather } from './hooks/useWeather';
import { useFavorites } from './hooks/useFavorites';

function App() {
  const [activeTab, setActiveTab] = useState('weather');
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [currentLocationName, setCurrentLocationName] = useState('San Mateo');
  const { location, setLocationFromCoords } = useLocation();
  const { weather, airQuality, pollen, loading, error, refetch } = useWeather(
    selectedLocation?.latitude || location?.latitude,
    selectedLocation?.longitude || location?.longitude
  );
  const { favorites, addFavorite, removeFavorite, isFavorite, clearAllFavorites } = useFavorites();

  // Set default location to San Mateo if no location is available
  useEffect(() => {
    if (!location && !selectedLocation) {
      setLocationFromCoords(37.5665, -122.3255); // San Mateo coordinates
    }
  }, [location, selectedLocation, setLocationFromCoords]);

  const handleLocationSelect = (locationData) => {
    setSelectedLocation(locationData);
    setCurrentLocationName(locationData.displayName);
  };

  const handleMapLocationClick = (coordinates) => {
    setLocationFromCoords(coordinates.latitude, coordinates.longitude);
    setSelectedLocation(null); // Clear search selection when using map
    setCurrentLocationName('Map Location');
  };

  const handleRefresh = () => {
    refetch();
  };

  const handleSettings = () => {
    // TODO: Implement settings modal
    console.log('Settings clicked');
  };

  const handleToggleFavorite = () => {
    const currentCoords = selectedLocation || location;
    if (currentCoords) {
      if (isFavorite(currentCoords.latitude, currentCoords.longitude)) {
        const favoriteId = `${currentCoords.latitude}-${currentCoords.longitude}`;
        removeFavorite(favoriteId);
      } else {
        addFavorite({
          name: currentLocationName,
          country: 'Selected Location',
          latitude: currentCoords.latitude,
          longitude: currentCoords.longitude,
          displayName: currentLocationName
        });
      }
    }
  };

  const handleFavoriteLocationSelect = (favorite) => {
    setSelectedLocation(favorite);
    setCurrentLocationName(favorite.displayName);
    setActiveTab('weather');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50">
      <Header activeTab={activeTab} setActiveTab={setActiveTab} />
      
      <main className="container mx-auto px-6 py-8">
        <AnimatePresence mode="wait">
          {activeTab === 'weather' && (
            <motion.div
              key="weather"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              transition={{ duration: 0.3 }}
              className="grid grid-cols-1 lg:grid-cols-3 gap-8"
            >
              {/* Left Column */}
              <div className="lg:col-span-2 space-y-8">
                <SearchBar onLocationSelect={handleLocationSelect} />
                <WeatherCard 
                  weather={weather} 
                  loading={loading} 
                  error={error}
                  locationName={currentLocationName}
                  onToggleFavorite={handleToggleFavorite}
                  isFavorite={isFavorite(
                    (selectedLocation || location)?.latitude, 
                    (selectedLocation || location)?.longitude
                  )}
                />
              </div>

              {/* Right Column */}
              <div className="space-y-8">
                <ControlButtons 
                  onRefresh={handleRefresh}
                  onSettings={handleSettings}
                />
                
                <InteractiveMap 
                  currentLocation={selectedLocation || location}
                  onLocationSelect={handleMapLocationClick}
                />
                
                <TodaysHighlights 
                  weather={weather}
                  airQuality={airQuality}
                  pollen={pollen}
                />
              </div>
            </motion.div>
          )}

          {activeTab === 'favorites' && (
            <FavoritesTab
              key="favorites"
              favorites={favorites}
              onLocationSelect={handleFavoriteLocationSelect}
              onRemoveFavorite={removeFavorite}
              onClearAll={clearAllFavorites}
            />
          )}
        </AnimatePresence>
      </main>
    </div>
  );
}

export default App;
